import { Box, Divider, Grid2, Typography } from "@mui/material";
import SeverityBadge from "./Badges/SeverityBadge";
import LevelBadge from "./Badges/LevelBadge";
import StatusBadge from "./Badges/StatusBadge";
import AlertCategoryBadge from "./Badges/AlertCategoryBadge";
import { convertAndGetTimeStampInRequiredFormat, getMetricTimeStamp } from "../../Helper/DatePicker/DateConverter";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import OverdueBadge from "./Badges/OverdueBadge";
import dayjs from "dayjs";
import CustomIcon from "../../Components/CustomIcon";
import { encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import { getFormattedResolutionTime } from "../../Helper/DatePicker/DateFormatters";
import SingleAlertAnalysisDialog from "./SingleAlertAnalysis/SingleAlertAnalysisDialog";
import { StyledAction } from "./Components/StyledComponents";
import { useState } from "react";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { ANNOTATION_SOURCE } from "../AdvancedAnalytics/Common/CommonFunctions";
import { buildAdvancedAnalysisParams } from "../AdvancedAnalytics/Common/advancedAnalysisNav";
import { computeActionLabel } from "../Alerts/alertActionLabel";

const AlertListItemIncident = ({ battery, selectedTab, alertIncident, onActionClick, showActions = true, showAnalyse = true, isPlatformAlert = false, analyseInfoFn, overdueThreshold = 7 }) => {
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const { hasPermission, isRbacV2Enabled } = usePermissions();
    const featureCode = isPlatformAlert ? FEATURE_CODES.PLATFORM_ALERTS_V2 : FEATURE_CODES.BMS_FAULTS_V2;
    const canUpdate = !isRbacV2Enabled() || hasPermission(featureCode, 'update');

    const { incidentErrorName, severity, severityLevel, bmsAlertStatus, category, customerDBTimeInsert, entityAffectedDisplayID, entityAffected, resolveCustomerDBTime = null, resolutionTime = null, overdueSince } = alertIncident;
    const triggeredOn = convertAndGetTimeStampInRequiredFormat(customerDBTimeInsert, "DD MMM'YY, hh:mm A");
    const relativeTriggeredOn = getMetricTimeStamp(customerDBTimeInsert, 'relative');
    const diffInDays = dayjs().diff(dayjs(convertAndGetTimeStampInRequiredFormat(customerDBTimeInsert)), "days");
    const showOverDueBadge = selectedTab === 'active' && overdueThreshold != null && diffInDays > overdueThreshold;

    const actionCtaLabel = computeActionLabel(bmsAlertStatus, canUpdate);

    let resolvedOn = null;
    let relativeResolvedOn = null;
    if (resolveCustomerDBTime) {
        resolvedOn = convertAndGetTimeStampInRequiredFormat(resolveCustomerDBTime, "DD MMM'YY, hh:mm A");
        relativeResolvedOn = getMetricTimeStamp(resolveCustomerDBTime, 'relative');
    }

    let resolvedDuration = null;
    if (resolutionTime) {
        resolvedDuration = getFormattedResolutionTime(resolutionTime);
    }

    const handleAnalyseOpen = () => setOpen(true);
    const handleAnalyseClose = () => setOpen(false);

    // Advanced Analysis is shown alongside the primary actions for BMS faults
    // (showActions). Smart alert listings render without those actions
    // (showActions=false) but should still expose Advanced Analysis wherever the
    // Analyse action is available.
    const showAdvancedAnalysis = showActions || (showAnalyse && isPlatformAlert);

    const handleAdvancedAnalyticsClick = () => {
        const temp = buildAdvancedAnalysisParams({
            battery,
            alertIncident,
            source: isPlatformAlert ? ANNOTATION_SOURCE.SMART_ALERT : ANNOTATION_SOURCE.BMS_FAULT,
        });
        encryptTheParams(temp, navigate, false, '/advanced-analysis', true);
    };

    return (
        <Box
            sx={{
                bgcolor: severity?.toLowerCase() === 'critical' ? '#FFF3F3' : '#F9F9F9',
                px: 2.5,
                py: 2,
                borderRadius: '3px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                border: '1px solid transparent',
                "&:hover": {
                    borderColor: '#D4D5FC'
                }
            }}
        >
            <Grid2 container gap={5} width={'100%'}>
                <Grid2 container flexDirection={'column'} justifyContent={'space-between'} alignItems={'flex-start'} gap={1} pt={0.75}>
                    <PrimaryTooltip
                        title={`Triggered ${relativeTriggeredOn}`}
                        placement="bottom"
                    >
                        <Typography variant="fw500s14c494949" sx={{ lineHeight: '20px' }}>{triggeredOn}</Typography>
                    </PrimaryTooltip>
                    {showOverDueBadge && <OverdueBadge overdueSince={overdueSince} />}
                </Grid2>
                <Grid2 container flexDirection={'column'} gap={1} flex={1}>
                    <Grid2 container alignItems={'center'} justifyContent={'space-between'}>
                        <Grid2 container gap={1.5} alignItems={'center'}>
                            <Typography variant="fw500s16lh20c000000">{incidentErrorName}</Typography>
                            <SeverityBadge severity={severity} />
                            {!isPlatformAlert && severityLevel && <LevelBadge level={severityLevel} />}
                        </Grid2>
                        <Grid2 container gap={1} alignItems={'center'}>
                            {bmsAlertStatus && <StatusBadge status={bmsAlertStatus} />}
                            {showActions && (
                                <>
                                    <StyledAction onClick={onActionClick} data-testid="alert-action-cta">
                                        <Typography variant="fw500s14c494949" sx={{ lineHeight: '20px' }}>{actionCtaLabel}</Typography>
                                    </StyledAction>
                                    <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />
                                </>
                            )}
                            {(!showActions && showAnalyse && bmsAlertStatus) && (
                                <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />
                            )}
                            {(showActions || showAnalyse) && (
                                <PrimaryTooltip
                                    placement="bottom"
                                    title="Analyse"
                                >
                                    <StyledAction onClick={handleAnalyseOpen} sx={{py: '7px', px: 1}}>
                                        <CustomIcon name="binocular-icon" fill='#494949' />
                                    </StyledAction>
                                </PrimaryTooltip>
                            )}
                            {showAdvancedAnalysis && (
                                <>
                                    <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />
                                    <PrimaryTooltip
                                        placement="bottom"
                                        title="Advanced Analysis"
                                    >
                                        <StyledAction sx={{ p: 0.75 }} onClick={handleAdvancedAnalyticsClick} data-testid="advanced-analysis-action">
                                            <CustomIcon name='advanced-analysis-settings' fill="#494949" />
                                        </StyledAction>
                                    </PrimaryTooltip>
                                </>
                            )}
                        </Grid2>
                    </Grid2>
                    <Grid2 container gap={1.5}>
                        <AlertCategoryBadge category={category} />
                        <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="fw400s14c8F8F8F" sx={{ color: '#686868', lineHeight: '20px' }}>Entity Affected:</Typography>
                            <Typography variant="fw500s14c494949" sx={{ lineHeight: '20px', textTransform: 'capitalize' }}>{entityAffected?.toLowerCase() === 'tms' ? 'TMS' : entityAffectedDisplayID}</Typography>
                        </Box>
                        {resolvedOn && (
                            <>
                                <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="fw400s14c8F8F8F" sx={{ color: '#686868', lineHeight: '20px' }}>Resolved On:</Typography>
                                    <PrimaryTooltip
                                        title={relativeResolvedOn}
                                        placement="bottom"
                                    >
                                        <Typography variant="fw500s14c494949" sx={{ lineHeight: '20px', textTransform: 'capitalize' }}>{resolvedOn}</Typography>
                                    </PrimaryTooltip>
                                </Box>
                            </>
                        )}
                        {resolutionTime ? (
                            <>
                                <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="fw400s14c8F8F8F" sx={{ color: '#686868', lineHeight: '20px' }}>Resolution Time:</Typography>
                                    <Typography variant="fw500s14c494949" sx={{ lineHeight: '20px', textTransform: 'capitalize' }}>{resolvedDuration}</Typography>
                                </Box>
                            </>
                        ) : (<></>)}
                    </Grid2>
                </Grid2>
            </Grid2>
            {open && (
                <SingleAlertAnalysisDialog
                    open={open}
                    onClose={handleAnalyseClose}
                    analyseData={{ battery, alertIncident }}
                    fetchInfoFn={analyseInfoFn}
                    isPlatformAlert={isPlatformAlert}
                />
            )}
        </Box>
    )
}

export default AlertListItemIncident;
